/**
 * DeleteAccountConfirmPage — GDPR Article 17 magic-link landing.
 * Issue #532, feature doc docs/canonical/features/future/v1.6-gdpr-self-service.md
 *
 * Route: /settings/delete-account/confirm?token=xxx
 *
 * Loaded after the user clicks the magic link in their deletion-confirm
 * email. Presents one final "Delete now" CTA — does NOT auto-confirm on
 * mount because deletion is irreversible and the user may have clicked
 * the link by accident.
 */

import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { TopBar, Button, OroLoader } from '../../components';
import { AlertTriangle, CheckCircle, AlertCircle } from 'lucide-react';
import { useConfirmAccountDeletion } from '../../hooks/useAccountManagement';
import { ApiError } from '../../lib/api';
import { clearQueuedCheckins } from '../../lib/offlineCheckin';
import { signalSignOut } from '../../lib/accountGuard';

type Status = 'awaiting_confirm' | 'confirming' | 'success' | 'invalid_token' | 'error';

export default function DeleteAccountConfirmPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = searchParams.get('token');

  const initialStatus: Status = token ? 'awaiting_confirm' : 'invalid_token';
  const [status, setStatus] = useState<Status>(initialStatus);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const confirmMutation = useConfirmAccountDeletion();

  const handleConfirm = async (): Promise<void> => {
    if (!token) return;
    setStatus('confirming');
    try {
      const { other_session_remains: otherSessionRemains } = await confirmMutation.mutateAsync(token);
      setStatus('success');
      // #532 Codex P2 review (PR #1847 cycle-5): previously we called
      // useAuth().logout() here, which delegates to globalLogout() →
      // window.location.assign('/'). That navigation fired BEFORE the
      // success state below could render — user saw spinner then home page,
      // never the "Account deleted, receipt sent" confirmation. The
      // success block + "Return home" CTA were unreachable.
      //
      // Fix: backend already cleared the HttpOnly cookie. We just need to
      // wipe client-side cache so a stale `useMe` doesn't repaint stale
      // user data while the user reads the success message. queryClient
      // doesn't navigate. The user clicks "Return home" when they're
      // ready, which then navigates via the existing onClick handler.
      queryClient.clear();
      // #3688: the session ended without lib/auth logout(), so drop the
      // offline check-in queue here too. #3881: not when the token was for
      // another account: this browser's session lives on, and so does its queue.
      if (!otherSessionRemains) {
        clearQueuedCheckins();
        signalSignOut();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setStatus('invalid_token');
          setErrorMessage(err.message);
          return;
        }
        setErrorMessage(err.message);
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
      setStatus('error');
    }
  };

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Delete Account" onBack={() => navigate('/settings/account')} />

      <div className="flex-1 flex items-center justify-center px-4 pt-14">
        <div className="w-full max-w-md">
          {status === 'awaiting_confirm' && (
            <div className="space-y-5 text-center">
              <AlertTriangle className="w-12 h-12 text-oro-error mx-auto" />
              <h1 className="text-text-primary font-semibold text-xl">
                Permanently delete your account?
              </h1>
              <p className="text-text-secondary text-sm">
                This is the last step. Clicking the button below will immediately and irreversibly delete
                your account.
              </p>
              <ul className="text-text-secondary text-sm space-y-1 list-disc list-inside text-left">
                <li>Your profile, RSVPs, check-ins, and personal data will be removed.</li>
                <li>
                  Your chat messages will remain in club history but attributed to{' '}
                  <span className="font-mono text-xs">[Deleted User]</span>.
                </li>
                <li>Events you created will transfer to the club owner.</li>
                <li>You will be signed out on every device.</li>
              </ul>
              <div className="flex flex-col gap-3 pt-2">
                <Button
                  variant="destructive"
                  size="lg"
                  onClick={handleConfirm}
                  disabled={confirmMutation.isPending}
                >
                  {confirmMutation.isPending ? 'Deleting…' : 'Delete my account now'}
                </Button>
                <Button
                  variant="ghost"
                  size="md"
                  onClick={() => navigate('/settings/account')}
                  disabled={confirmMutation.isPending}
                >
                  Cancel — keep my account
                </Button>
              </div>
            </div>
          )}

          {status === 'confirming' && (
            <div className="text-center space-y-4">
              <OroLoader className="mx-auto" decorative />
              <p className="text-text-secondary text-sm">Deleting your account…</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center space-y-5">
              <CheckCircle className="w-12 h-12 text-oro-success mx-auto" />
              <h1 className="text-text-primary font-semibold text-xl">Account deleted</h1>
              <p className="text-text-secondary text-sm">
                Your account has been deleted. We sent a confirmation receipt to your email with details
                of what happened. Thank you for using ORO move.
              </p>
              <Button variant="primary" size="md" onClick={() => navigate('/')}>
                Return home
              </Button>
            </div>
          )}

          {status === 'invalid_token' && (
            <div className="text-center space-y-5">
              <AlertCircle className="w-12 h-12 text-oro-warning mx-auto" />
              <h1 className="text-text-primary font-semibold text-xl">
                Confirmation link invalid or expired
              </h1>
              <p className="text-text-secondary text-sm">
                {errorMessage ??
                  'This confirmation link is no longer valid. Links expire 15 minutes after they are sent and can only be used once.'}
              </p>
              <Button variant="primary" size="md" onClick={() => navigate('/settings/account')}>
                Request a new link
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center space-y-5">
              <AlertCircle className="w-12 h-12 text-oro-error mx-auto" />
              <h1 className="text-text-primary font-semibold text-xl">Something went wrong</h1>
              <p className="text-text-secondary text-sm">
                {errorMessage ??
                  'We could not complete the deletion. Your account is unchanged. Please try again or contact support.'}
              </p>
              <div className="flex flex-col gap-3">
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => {
                    setStatus('awaiting_confirm');
                    setErrorMessage(null);
                  }}
                >
                  Try again
                </Button>
                <Button variant="ghost" size="md" onClick={() => navigate('/settings/account')}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
